import { Suspense, lazy, useEffect, useRef } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Navigate, useLocation, Link } from "react-router-dom";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";

// BUGFIX + PERFORMANCE: Component that handles route change side effects
// - Clears stuck scroll locks from dialogs
// - Prefetches data for likely next pages (matches → React Query for instant load)
// - Prefetch matches immediately on mount when user is logged in (faster /matches load)
function RouteEffects() {
  const location = useLocation();

  useEffect(() => {
    // Clear stuck scroll locks on route change (no polling needed)
    const hasOpenDialog = document.querySelector('[role="dialog"]');
    if (!hasOpenDialog) {
      document.body.style.overflow = "";
      document.body.style.paddingRight = "";
      document.body.removeAttribute("data-scroll-locked");
    }
  }, [location.pathname]);

  return null;
}
import { GDPRConsentManager } from "@/components/GDPRConsentManager";
import { ApplyAttemptRealtimeProvider } from "@/components/ApplyAttemptRealtimeProvider";
import { ErrorBoundary } from "@/components/ui/error-boundary";
import { NetworkStatusIndicator } from "@/components/NetworkStatusIndicator";
import { FindigoChatProvider } from "@/components/findigo-chat/FindigoChatProvider";
import { FindigoChatWidget } from "@/components/findigo-chat/FindigoChatWidget";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { preloadRoutes } from "@/lib/routePreloader";
import { clearPrefetchCache } from "@/lib/dataPrefetcher";
import { clearSbKeys } from "@/lib/authStorage";
import { logInfo } from "@/lib/logger";
import { supabase } from "@/integrations/supabase/client";
import { useAdminAccess } from "@/hooks/useAdminAccess";

// PERFORMANCE: Lazy load ALL routes for optimal bundle splitting
// This reduces initial JS from ~1MB to ~450KB
const Index = lazy(() => import("./pages/Index"));
const Dashboard = lazy(() => import("./pages/Dashboard"));

// Lazy load other routes for code splitting
const Auth = lazy(() => import("./pages/Auth"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Onboarding = lazy(() => import("./pages/Onboarding"));
const Matches = lazy(() => import("./pages/Matches"));
const SavedJobs = lazy(() => import("./pages/SavedJobs"));
const Settings = lazy(() => import("./pages/Settings"));
const Feedback = lazy(() => import("./pages/Feedback"));
const Applications = lazy(() => import("./pages/Applications"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const PostAssignment = lazy(() => import("./pages/PostAssignment"));
const CreateFreelancerProfile = lazy(() => import("./pages/CreateFreelancerProfile"));
const Freelancers = lazy(() => import("./pages/Freelancers"));
const AdminLearning = lazy(() => import("./pages/AdminLearning"));
const AdminAlerts = lazy(() => import("./pages/AdminAlerts"));
const SentryVerify = lazy(() => import("./pages/SentryVerify"));
const NotFound = lazy(() => import("./pages/NotFound"));

// P0 E2E: Fallback when Dashboard (or route) throws – nav + terminal state so tests see main/nav and data-dashboard-state.
// error: set by wrapper's onError + setState so it's available after re-render (and in E2E via data-route-error).
const RouteErrorFallback = ({ error }: { error?: Error | null }) => {
  const errorText = error ? `${error.name}: ${error.message}` : "";
  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-background" role="banner">
        <nav className="container flex h-12 items-center gap-4 px-4" role="navigation" aria-label="Huvudnavigering">
          <Link to="/" className="text-sm font-medium text-foreground hover:underline">Startsida</Link>
          <Link to="/dashboard" className="text-sm font-medium text-foreground hover:underline">Hitta jobb</Link>
          <Link to="/matches" className="text-sm font-medium text-foreground hover:underline">Matchningar</Link>
          <Link to="/settings" className="text-sm font-medium text-foreground hover:underline">Inställningar</Link>
        </nav>
      </header>
      <main id="main-content" className="page-container pt-4 pb-20" role="main" data-testid="app-main">
        <section
          aria-label="Alla jobb"
          data-dashboard-state="error"
          data-testid="dashboard-job-list"
          data-route-error={errorText || undefined}
        >
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <h2 className="text-lg font-semibold mb-2">Något gick fel</h2>
            <p className="text-sm text-muted-foreground mb-4 max-w-md">Sidan kunde inte laddas. Försök igen eller gå till startsidan.</p>
            {error && (
              <details className="mb-4 text-left max-w-2xl w-full" open data-testid="route-error-details-container">
                <summary className="cursor-pointer text-sm font-medium text-muted-foreground">Visa fel</summary>
                <pre className="mt-2 p-3 bg-muted rounded text-xs overflow-auto whitespace-pre-wrap break-words" data-testid="route-error-details">
                  {error.name}: {error.message}
                  {error.stack && `\n\n${error.stack}`}
                </pre>
              </details>
            )}
            <Button type="button" variant="outline" onClick={() => window.location.reload()} className="min-h-[var(--touch-target)]">Ladda om</Button>
          </div>
        </section>
      </main>
    </div>
  );
};

// Wrapper: pass fallback as function so ErrorBoundary can pass caught error on first render (E2E gets data-route-error).
function DashboardRouteWithErrorBoundary() {
  return (
    <ErrorBoundary
      onError={() => {}}
      fallback={(error) => <RouteErrorFallback error={error ?? undefined} />}
    >
      <Dashboard />
    </ErrorBoundary>
  );
}

// Optimized QueryClient with better defaults for performance
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 2 * 60 * 1000, // 2 minutes - avoid refetch on navigation
      gcTime: 10 * 60 * 1000, // 10 minutes cache
      refetchOnWindowFocus: false,
      refetchOnMount: false, // Use cache on mount - critical for fast navigation
      refetchOnReconnect: false,
      retry: 1,
      retryDelay: 1000,
    },
  },
});

// PERFORMANCE: Minimal fallback - null for instant render, or minimal skeleton
// Critical routes (Index, Dashboard) are eager loaded, so Suspense rarely triggers
const PageLoader = () => null; // Instant render - no loading screen

const AdminRoute = ({ children }: { children: JSX.Element }) => {
  const { isAdmin, loading } = useAdminAccess();
  if (loading) return null;
  return isAdmin ? children : <Navigate to="/dashboard" replace />;
};

const App = () => {
  // NOTE: ConfigErrorScreen removed - Supabase config is now hardcoded in client.ts
  // No need to check for missing environment variables anymore
  
  // OBSERVABILITY: Initialize Sentry if DSN is available (prod or preview)
  // DSN: VITE_SENTRY_DSN vid build, eller __RUNTIME_CONFIG__.SENTRY_DSN om host sätter det
  // Note: Initierar även i preview/bygge om DSN finns (Lovable preview kan ha DSN)
  if (typeof window !== 'undefined') {
    const cfg = window.__RUNTIME_CONFIG__;
    const dsn = (typeof cfg?.SENTRY_DSN === 'string' && cfg.SENTRY_DSN.trim()) ? cfg.SENTRY_DSN : import.meta.env.VITE_SENTRY_DSN;
    // Initiera om DSN finns (oavsett PROD/DEV - Lovable preview kan ha DSN)
    if (dsn && typeof dsn === 'string' && dsn.trim()) {
      import('@/lib/sentry').then(({ initSentry }) => {
        initSentry({
          dsn,
          environment: import.meta.env.VITE_ENVIRONMENT || (import.meta.env.PROD ? 'production' : 'preview'),
          release: import.meta.env.VITE_RELEASE_VERSION,
        });
      }).catch((err) => {
        if (import.meta.env.DEV) {
          console.error("[Sentry] Init failed:", err);
        }
      });
    }

    // PERFORMANCE: Initialize Web Vitals tracking
    import('@/lib/webVitals').then(async ({ initWebVitals }) => {
      await initWebVitals();
    }).catch(() => {
      // Web Vitals init failed - not critical (package might not be installed yet)
      // This is handled gracefully in webVitals.ts
    });
  }
  
  // PERFORMANCE: Preload critical routes on app start (target <0.3s perceived load).
  useEffect(() => {
    preloadRoutes(['/', '/dashboard', '/matches', '/auth', '/saved-jobs', '/settings', '/applications']);
  }, []);

  // BUGFIX: Clear stuck scroll locks on app mount
  // If a dialog crashes or doesn't cleanup properly, body.overflow="hidden" can persist
  // This ensures scroll works when the app loads (no dialogs should be open on mount)
  useEffect(() => {
    const isScrollLocked = document.body.style.overflow === "hidden" || 
                           document.body.getAttribute("data-scroll-locked") === "1";
    if (isScrollLocked) {
      logInfo("[App] Clearing stuck scroll lock on mount");
      document.body.style.overflow = "";
      document.body.style.paddingRight = "";
      document.body.removeAttribute("data-scroll-locked");
    }
  }, []);

  // Scroll-lock cleanup handled by RouteEffects on navigation

  // Auth: self-heal invalid session + guard stale auth callback (once per load)
  useEffect(() => {
    const path = window.location.pathname || "/";
    const search = window.location.search || "";
    const hash = window.location.hash || "";
    const isResetPassword = /^\/reset-password\/?$/.test(path);
    const hasAuthCallback =
      !isResetPassword &&
      (search.includes("code=") || /access_token=|refresh_token=/.test(hash));

    if (hasAuthCallback) {
      // OAuth/magic-link return: wait for Supabase to set session from hash, then clean URL. Do NOT run self-heal (it would signOut on stale session).
      const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
        if (!session) return;
        const u = new URL(window.location.href);
        u.hash = "";
        u.searchParams.delete("code");
        u.searchParams.delete("error");
        u.searchParams.delete("error_description");
        history.replaceState(null, "", u.search ? u.pathname + u.search : u.pathname);
        subscription?.unsubscribe?.();
      });
      // 15s: allow time for PKCE code exchange and network latency; CSP-blocked inline script would still fail, but PKCE avoids that
      const t = setTimeout(() => {
        subscription?.unsubscribe?.();
        supabase.auth.getSession().then(({ data: { session } }) => {
          if (session) return;
          clearSbKeys();
          clearPrefetchCache();
          supabase.auth.signOut({ scope: "local" }).then(() => {
            window.location.href = "/auth?session_expired=1";
          });
        });
      }, 15000);
      return () => {
        clearTimeout(t);
        subscription?.unsubscribe?.();
      };
    }

    // App init self-heal: 401 / invalid JWT → signOut, redirect only if not already on /auth. Skip when URL has auth callback (Supabase is about to set session from hash).
    // P0: 504 on token refresh (Supabase Auth Gateway Timeout) → redirect to /auth?auth_timeout=1 so user can log in again.
    if (!hasAuthCallback) {
      (async () => {
        const path = window.location.pathname || "";
        const isAuthPage = path.includes("/auth");
        try {
          const {
            data: { session },
            error: sessionError,
          } = await supabase.auth.getSession();
          // 504 / timeout during token refresh → redirect so user can log in again (Supabase Auth Gateway Timeout)
          if (sessionError) {
            const errMsg = String((sessionError as { message?: string })?.message ?? "").toLowerCase();
            const is504 = errMsg.includes("504") || errMsg.includes("gateway timeout") || errMsg.includes("timeout");
            clearSbKeys();
            clearPrefetchCache();
            await supabase.auth.signOut({ scope: "local" }).catch((e) => { if (import.meta.env.DEV) console.warn("[App] signOut after session error:", e); });
            if (!isAuthPage) {
              window.location.href = is504 ? "/auth?auth_timeout=1" : "/auth?session_expired=1";
            }
            return;
          }
          if (!session) return;
          const { error } = await supabase.auth.getUser();
          if (!error) return;
          const msg = String((error as { message?: string }).message || "").toLowerCase();
          const code = (error as { status?: number }).status;
          const is401 = code === 401;
          const isInvalidJwt = /invalid|jwt|refresh|session|token|unauthorized/.test(msg);
          if (is401 || isInvalidJwt) {
            clearSbKeys();
            clearPrefetchCache();
            await supabase.auth.signOut({ scope: "local" });
            if (!isAuthPage) {
              window.location.href = "/auth?session_expired=1";
            }
          }
        } catch (e) {
          // AuthRetryableFetchError / 504 from refresh_token – redirect so user can log in again
          const msg = String(e instanceof Error ? e.message : e).toLowerCase();
          const is504 = msg.includes("504") || msg.includes("timeout") || msg.includes("gateway");
          clearSbKeys();
          clearPrefetchCache();
          supabase.auth.signOut({ scope: "local" }).catch((e) => { if (import.meta.env.DEV) console.warn("[App] signOut after auth catch:", e); });
          if (!isAuthPage) {
            window.location.href = is504 ? "/auth?auth_timeout=1" : "/auth?session_expired=1";
          }
        }
      })();
    }
  }, []);

  // CSP-safe: push session to Findigo extension via postMessage (no inline script injection on findigo.se)
  const EXTENSION_SESSION_KEY = 'sb-vhiplwmomkjcgtponbvy-auth-token';
  const EXTENSION_MESSAGE_TYPE = 'FINDIGO_EXTENSION_SESSION';
  useEffect(() => {
    function pushSessionToExtension() {
      try {
        const raw = window.localStorage?.getItem(EXTENSION_SESSION_KEY);
        if (raw && raw.length > 0) {
          window.postMessage({ type: EXTENSION_MESSAGE_TYPE, payload: raw }, '*');
        }
      } catch {
        /* ignore */
      }
    }
    const { data: { subscription } } = supabase.auth.onAuthStateChange(() => {
      pushSessionToExtension();
    });
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) pushSessionToExtension();
    });
    // P0: Delayed push so extension content script (document_idle) has time to attach listener
    const delayedPush = setTimeout(() => pushSessionToExtension(), 800);
    // P0: Re-push when tab becomes visible so extension picks up session after user switches back to findigo.se
    const onVisibility = () => {
      if (document.visibilityState === 'visible') pushSessionToExtension();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      clearTimeout(delayedPush);
      subscription?.unsubscribe?.();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  if (import.meta.env.DEV) {
    logInfo("[App] Initializing", {
      configSource: "env",
    });
  }
  
  // Wrap everything in ErrorBoundary to catch any initialization errors
  return (
    <ErrorBoundary
      fallback={
        <div className="min-h-screen flex items-center justify-center bg-background p-4">
          <div className="max-w-md w-full space-y-4 text-center">
            <h1 className="text-2xl font-bold text-foreground">Kritiskt fel</h1>
            <p className="text-muted-foreground">
              Applikationen kunde inte starta. Försök ladda om sidan eller kontakta support.
            </p>
            <Button type="button" onClick={() => window.location.reload()} variant="default" className="min-h-[var(--touch-target)]" aria-label="Ladda om sidan">
              Ladda om sidan
            </Button>
            {import.meta.env.DEV && (
              <details className="mt-4 text-left">
                <summary className="cursor-pointer text-sm text-muted-foreground">
                  Debug info
                </summary>
                <pre className="mt-2 p-2 bg-muted rounded text-xs overflow-auto">
                  {JSON.stringify({
                    env: {
                      hasSupabaseUrl: true, // Now hardcoded in client.ts
                      hasSupabaseKey: true, // Now hardcoded in client.ts
                    },
                    supabaseUrl: 'https://vhiplwmomkjcgtponbvy.supabase.co',
                  }, null, 2)}
                </pre>
              </details>
            )}
          </div>
        </div>
      }
    >
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <FindigoChatProvider>
      <Toaster />
      <BrowserRouter>
        <RouteEffects />
        <ApplyAttemptRealtimeProvider />
            <Suspense fallback={<PageLoader />}>
              <ErrorBoundary fallback={<RouteErrorFallback />}>
        <Routes>
          <Route path="/" element={<Index />} />
          <Route path="/auth" element={<Auth />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/onboarding" element={<Onboarding />} />
          <Route path="/dashboard" element={<DashboardRouteWithErrorBoundary />} />
          <Route path="/matches" element={<Matches />} />
          <Route path="/saved-jobs" element={<SavedJobs />} />
          <Route path="/saved" element={<Navigate to="/saved-jobs" replace />} />
          <Route path="/applications" element={<Applications />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/feedback" element={<Feedback />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/post-assignment" element={<PostAssignment />} />
          <Route path="/create-profile" element={<CreateFreelancerProfile />} />
          <Route path="/freelancers" element={<Freelancers />} />
          <Route
            path="/admin/learning"
            element={
              <AdminRoute>
                <AdminLearning />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/alerts"
            element={
              <AdminRoute>
                <AdminAlerts />
              </AdminRoute>
            }
          />
          <Route path="/sentry-verify" element={<SentryVerify />} />
          {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
          <Route path="*" element={<NotFound />} />
        </Routes>
              </ErrorBoundary>
            </Suspense>
        <GDPRConsentManager />
        <NetworkStatusIndicator />
        <FindigoChatWidget />
      </BrowserRouter>
      </FindigoChatProvider>
    </TooltipProvider>
  </QueryClientProvider>
    </ErrorBoundary>
);
};

export default App;
